import {AsyncPage} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {
  getEnrollmentApplications,
  EnrollmentApplicationTemplateType,
  changeEnrollmentApplicationFilters,
  clearEnrollmentFileApplications,
  changeSort,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import {MenuItem} from '@material-ui/core';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import useReactRouter from 'use-react-router';
import {BrokerSpreadsheetStatus} from './BrokerSpreadsheetStatus';
import {GenelcoSpreadsheetStatus} from './GenelcoSpreadsheetStatus';
import {showModal, ModalState} from 'Core/ducks/modal';
import {EnrollmentFileStatusType} from 'Enrollment/models/EnrollmentFileStatusType';
import {Scope} from 'Shared/constants/Scope';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {
  withDateFilterProperties,
  withInequalityFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import * as O from 'fp-ts/lib/Option';

type EnrollmentFilesProps = {
  enrollmentFileMetaData: AsyncPage<Page<EnrollmentApplication>>;
};

const UploadedEnrollmentApplicationsTable = (props: EnrollmentFilesProps) => {
  const dispatch = useDispatch();
  const {history} = useReactRouter();
  const scopes = useSelector(selectUserScopes);
  const {enrollmentApplicationsFilters, sort} = useSelector((state: ReducerState) => state.enrollmentFileProcessing);
  const hasScopeToCancelFileUpload = (scopes: Scope[]) => scopes.some(scope => scope === Scope.CancelFileUpload);

  const changeFilter = withValueChangedHook(
    enrollmentApplicationsFilters,
    changeEnrollmentApplicationFilters,
    dispatch
  );
  const filterProperties = {
    changeFilter,
    filterData: enrollmentApplicationsFilters,
  };
  return (
    <PagedView
      pagedViewKey="uploadedEnrollmentApplicationsTable"
      data={props.enrollmentFileMetaData}
      sort={O.toUndefined(sort)}
      onChangeSort={sort => dispatch(changeSort(sort))}
      loadMore={page => dispatch(getEnrollmentApplications(page, enrollmentApplicationsFilters))}
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="File Type"
            default
            filterProperties={withStringFilterProperties(filterProperties, 'fileType')}
          >
            {enrollmentFiles => (
              <div className="table__table-cell-content">
                <div
                  className={
                    enrollmentFiles.fileType === '.xls'
                      ? 'file-type-badge file-type-badge__xls'
                      : enrollmentFiles.fileType === '.pdf'
                      ? 'file-type-badge file-type-badge__pdf'
                      : 'file-type-badge file-type-badge__xlsx'
                  }
                >
                  {' '}
                  {enrollmentFiles.fileType?.substr(1)}
                </div>
              </div>
            )}
          </Column>
          <Column
            {...columnProps}
            title="Name"
            default
            sortColumnKey="fileName"
            filterProperties={withStringFilterProperties(filterProperties, 'fileName')}
          >
            {enrollmentFiles => enrollmentFiles.fileName}
          </Column>
          <Column
            {...columnProps}
            title="Uploaded Date"
            default
            sortColumnKey="fileUploadedDate"
            filterProperties={withDateFilterProperties(filterProperties, 'fileUploadedDate')}
          >
            {enrollmentFiles => toFormattedDateTime(enrollmentFiles.fileUploadedDate!)}
          </Column>
          <Column
            {...columnProps}
            title="Size"
            default
            sortColumnKey="fileSize"
            filterProperties={withStringFilterProperties(filterProperties, 'fileSize')}
          >
            {enrollmentFiles => enrollmentFiles.fileSize}
          </Column>
          <Column
            {...columnProps}
            title="Row Count"
            default
            sortColumnKey="rowCount"
            filterProperties={withInequalityFilterProperties(filterProperties, 'rowCount')}
          >
            {enrollmentFiles => (enrollmentFiles.fileType === '.pdf' ? '--' : enrollmentFiles.rowCount)}
          </Column>
          <Column
            {...columnProps}
            title="Format"
            default
            sortColumnKey="templateType"
            filterProperties={withStringFilterProperties(filterProperties, 'templateType')}
          >
            {enrollmentFiles => (enrollmentFiles.fileType === '.pdf' ? 'Pdf' : enrollmentFiles.templateType?.name)}
          </Column>
          <Column {...columnProps} title="Status" default>
            {enrollmentFiles =>
              enrollmentFiles.templateType?.id! === EnrollmentApplicationTemplateType.BrokerSpreadsheet ? (
                <BrokerSpreadsheetStatus
                  fileStatus={enrollmentFiles.fileStatus!}
                  invalidRecordCount={enrollmentFiles.invalidRecordCount!}
                  validRecordCount={enrollmentFiles.validRecordCount!}
                  rowCount={enrollmentFiles.rowCount!}
                  fileStatusIdBeforeCanceled={enrollmentFiles.fileStatusIdBeforeCanceled!}
                />
              ) : (
                <GenelcoSpreadsheetStatus
                  id={enrollmentFiles.fileStatus!.id!}
                  invalidRecordCount={enrollmentFiles.invalidRecordCount!}
                  validRecordCount={enrollmentFiles.validRecordCount!}
                  rowCount={enrollmentFiles.rowCount!}
                  invalidAuditCount={enrollmentFiles.invalidAuditCount!}
                  validAuditCount={enrollmentFiles.validAuditCount!}
                  groupWebComparisonErrorCount={enrollmentFiles.groupWebComparisonErrorCount!}
                  isValidRecord={enrollmentFiles.isValidRecord!}
                />
              )
            }
          </Column>
          <Column
            {...columnProps}
            title="Status Date"
            default
            sortColumnKey="fileStatusChangedDate"
            filterProperties={withDateFilterProperties(filterProperties, 'fileStatusChangedDate')}
          >
            {enrollmentFiles => toFormattedDateTime(enrollmentFiles.fileStatusChangedDate!)}
          </Column>
          <Column {...columnProps} title="Actions" isActions default>
            {enrollmentFiles => (
              <>
                <ActionsMenu>
                  <MenuItem
                    id="menu-item-actions-uploaded-enrollment-applications-view-details"
                    onClick={
                      enrollmentFiles.templateType?.id === EnrollmentApplicationTemplateType.BrokerSpreadsheet
                        ? () =>
                            history.push(
                              EnrollmentRouteConstants.BROKER_SPREADSHEET_STATUS_DETAILS(enrollmentFiles.id!)
                            )
                        : enrollmentFiles.isValidRecord
                        ? () =>
                            history.push(
                              EnrollmentRouteConstants.ENROLLMENT_GENELCOSHEET_STATUS_DETAILS(enrollmentFiles.id!)
                            )
                        : () => dispatch(showModal(ModalState.GenelcoUploadInternalSystemError()))
                    }
                  >
                    View Details
                  </MenuItem>
                  {hasScopeToCancelFileUpload(scopes) &&
                    enrollmentFiles.templateType?.id === EnrollmentApplicationTemplateType.BrokerSpreadsheet &&
                    enrollmentFiles.fileStatus?.id === EnrollmentFileStatusType.RECEIVED && (
                      <MenuItem
                        color="primary"
                        onClick={() =>
                          dispatch(
                            showModal(
                              ModalState.BrokerSpreadSheetCancelConfirmation({
                                fileId: enrollmentFiles.id!,
                              })
                            )
                          )
                        }
                      >
                        Cancel
                      </MenuItem>
                    )}
                  {enrollmentFiles.templateType?.id === EnrollmentApplicationTemplateType.GenelcoSpreadsheet &&
                  enrollmentFiles.fileStatus?.id !== EnrollmentFileStatusType.COMPLETED &&
                  enrollmentFiles.fileStatus?.id !== EnrollmentFileStatusType.CANCELED ? (
                    <MenuItem
                      color="primary"
                      onClick={() =>
                        dispatch(
                          showModal(
                            ModalState.SimplifiedEnrollmentFileUploadCancelConfirmation({
                              fileId: enrollmentFiles.id!,
                            })
                          )
                        )
                      }
                    >
                      Cancel
                    </MenuItem>
                  ) : (
                    ''
                  )}
                </ActionsMenu>
              </>
            )}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default UploadedEnrollmentApplicationsTable;
